// Token-minter contract model (CONSOLE-TOKEN-MINTER-SCAFFOLD). Pins Codex's hosted
// public.mint_checkpoint_tokens_for_route contract as a PURE request builder - the scaffold
// surface never executes it (zero-rpc test-pinned). Live minting arrives in the next slice
// behind an explicit confirmation flow, once Codex confirms the print-kit handling plan and
// (ideally) seeds a tokens.mint capability key for honest UI gating.
//
// Raw-token doctrine carried from the field-proof lane: mint responses contain raw
// checkpoint tokens that are ONE-TIME print/export material - they must never live in
// persistent UI state, storage, analytics, logs, git, docs, or the shared workspace.

export type MintTokenType = "QR" | "NFC";

export interface MintRequestOptions {
  tokenType?: MintTokenType;
  replaceExisting?: boolean;
  humanCodePrefix?: string | null;
}

export interface MintRequest {
  fn: "mint_checkpoint_tokens_for_route";
  params: {
    p_account_id: string;
    p_route_id: string;
    p_token_type: MintTokenType;
    p_replace_existing: boolean;
    p_human_code_prefix: string | null;
  };
}

export interface SingleCheckpointMintRequestOptions {
  tokenType?: MintTokenType;
  replaceExisting?: boolean;
  humanCode?: string | null;
}

export interface SingleCheckpointMintRequest {
  fn: "mint_checkpoint_token";
  params: {
    p_account_id: string;
    p_checkpoint_id: string;
    p_token_type: MintTokenType;
    p_replace_existing: boolean;
    p_human_code: string | null;
  };
}

/** Build the exact hosted-RPC request shape. Pure - callers decide when to execute. */
export function buildMintRequest(
  accountId: string,
  routeId: string,
  options: MintRequestOptions = {},
): MintRequest {
  return {
    fn: "mint_checkpoint_tokens_for_route",
    params: {
      p_account_id: accountId,
      p_route_id: routeId,
      p_token_type: options.tokenType ?? "QR",
      p_replace_existing: options.replaceExisting ?? false,
      p_human_code_prefix: options.humanCodePrefix ?? null,
    },
  };
}

/** Build the exact hosted single-checkpoint recovery RPC request shape. */
export function buildSingleCheckpointMintRequest(
  accountId: string,
  checkpointId: string,
  options: SingleCheckpointMintRequestOptions = {},
): SingleCheckpointMintRequest {
  return {
    fn: "mint_checkpoint_token",
    params: {
      p_account_id: accountId,
      p_checkpoint_id: checkpointId,
      p_token_type: options.tokenType ?? "QR",
      p_replace_existing: options.replaceExisting ?? false,
      p_human_code: options.humanCode ?? null,
    },
  };
}

/** Single safe generic for mint failures - raw RPC errors never render. */
export const SAFE_MINT_FAILURE = "Couldn't mint checkpoint material — please try again.";

// Contract-stable, value-free failure sentences from the hosted mint RPC (20260606192022).
// EXACT matches render verbatim (operator-actionable); anything else fails closed.
const KNOWN_MINT_FAILURES: ReadonlySet<string> = new Set([
  "Only account admins and supervisors can mint checkpoint tokens.",
  "Human code prefix is invalid.",
  "Human code is invalid.",
  "Route does not belong to the requested account.",
  "Checkpoint does not belong to the requested account.",
  "Checkpoint is not active.",
  "Active checkpoint tokens already exist for this route. Set replace_existing to mint replacements.",
  "Active checkpoint token already exists. Set replace_existing to mint a replacement.",
  "Route has no active checkpoints to mint.",
]);

/**
 * One minted kit row. `token` is RAW one-time material: shown in the kit modal and kept only
 * in this browser tab's session ledger until the tab closes. Never localStorage, analytics,
 * logs, committed docs, or workspace files.
 */
export interface MintedToken {
  routeOrder: number;
  checkpointId: string;
  routeId?: string | undefined;
  checkpointName: string;
  humanCode: string;
  token: string;
  tokenType: MintTokenType;
  status: string;
}

export type MintOutcome = { ok: true; tokens: MintedToken[] } | { ok: false; reason: string };

function parseMintRows(data: unknown): MintOutcome {
  if (!Array.isArray(data)) {
    return { ok: false, reason: SAFE_MINT_FAILURE };
  }
  const tokens: MintedToken[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    if (typeof raw.token !== "string" || raw.token.length === 0) {
      continue; // a kit slot without printable material is worse than a smaller kit
    }
    tokens.push({
      routeOrder: typeof raw.route_order === "number" ? raw.route_order : tokens.length + 1,
      checkpointId: typeof raw.checkpoint_id === "string" ? raw.checkpoint_id : "",
      routeId: typeof raw.route_id === "string" ? raw.route_id : undefined,
      checkpointName: typeof raw.checkpoint_name === "string" ? raw.checkpoint_name : "Checkpoint",
      humanCode: typeof raw.human_code === "string" ? raw.human_code : "",
      token: raw.token,
      tokenType: raw.token_type === "NFC" ? "NFC" : "QR",
      status: typeof raw.status === "string" ? raw.status : "active",
    });
  }
  if (tokens.length === 0) {
    return { ok: false, reason: SAFE_MINT_FAILURE };
  }
  tokens.sort((a, b) => a.routeOrder - b.routeOrder);
  const usable = tokens.filter((token) => token.checkpointId.length > 0);
  if (usable.length === 0) {
    return { ok: false, reason: SAFE_MINT_FAILURE };
  }
  return { ok: true, tokens: usable };
}

/** Execute a built mint request against the hosted RPC. Never throws. */
export async function executeMintRequest(
  client: import("@supabase/supabase-js").SupabaseClient,
  request: MintRequest,
): Promise<MintOutcome> {
  try {
    const { data, error } = await client.rpc(request.fn, request.params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_MINT_FAILURES.has(message) ? message : SAFE_MINT_FAILURE };
    }
    return parseMintRows(data);
  } catch {
    return { ok: false, reason: SAFE_MINT_FAILURE };
  }
}

/** Execute a built single-checkpoint recovery request against the hosted RPC. Never throws. */
export async function executeSingleCheckpointMintRequest(
  client: import("@supabase/supabase-js").SupabaseClient,
  request: SingleCheckpointMintRequest,
): Promise<MintOutcome> {
  try {
    const { data, error } = await client.rpc(request.fn, request.params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_MINT_FAILURES.has(message) ? message : SAFE_MINT_FAILURE };
    }
    return parseMintRows(data);
  } catch {
    return { ok: false, reason: SAFE_MINT_FAILURE };
  }
}

/** Operator-facing one-time handling rules. Value-free copy - rendered verbatim. */
export const RAW_TOKEN_HANDLING_RULES: readonly string[] = [
  "Each mint creates one checkpoint secret. Print it as a QR label and write the same value to an NFC tag.",
  "After minting, use View kit to reopen QR and NFC values minted in this browser tab.",
  "Raw secrets are not stored on the server. This tab keeps them in session storage until you close the browser tab.",
  "The Active checkpoint material list shows what is on file (counts and types) without re-displaying secrets.",
  "Replacing retires active material and issues a new secret. Old tags stop working the moment replacements are minted.",
  "Only account admins and supervisors can mint; the server enforces this even if this screen is reached another way.",
];
